/**
 * Mock WebSocket Service for Demo Mode
 *
 * Simulates Socket.io events to create real-time updates in demo mode
 */

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

class MockSocket {
  constructor() {
    this.listeners = {};
    this.isConnected = false;
    this.simulationIntervals = [];
  }

  on(event, callback) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);

    console.log(`[DEMO MODE] Socket listener registered for: ${event}`);

    // Auto-connect when first listener is added
    if (!this.isConnected) {
      this.connect();
    }

    return this;
  }

  off(event, callback) {
    if (this.listeners[event]) {
      this.listeners[event] = this.listeners[event].filter((cb) => cb !== callback);
    }
    return this;
  }

  emit(event, data) {
    console.log(`[DEMO MODE] Socket emit (no-op in demo):`, event, data);
    // In demo mode, emit does nothing (read-only)
    return this;
  }

  connect() {
    if (this.isConnected) return;

    console.log('[DEMO MODE] Mock socket connected');
    this.isConnected = true;

    // Trigger connect event
    this.trigger('connect');

    // Start simulation loops
    this.startSimulations();
  }

  disconnect() {
    console.log('[DEMO MODE] Mock socket disconnected');
    this.isConnected = false;

    // Clear all simulation intervals
    this.simulationIntervals.forEach((interval) => clearInterval(interval));
    this.simulationIntervals = [];

    this.trigger('disconnect');
  }

  trigger(event, data) {
    if (this.listeners[event]) {
      this.listeners[event].forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`[DEMO MODE] Error in socket listener for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Start simulation intervals for various real-time events
   */
  startSimulations() {
    // Simulate route progress updates every 10 seconds
    const routeProgressInterval = setInterval(() => {
      this.simulateRouteProgress();
    }, 10000);
    this.simulationIntervals.push(routeProgressInterval);

    // Simulate order status updates every 15 seconds
    const orderStatusInterval = setInterval(() => {
      this.simulateOrderStatusUpdate();
    }, 15000);
    this.simulationIntervals.push(orderStatusInterval);
  }

  /**
   * Simulate route progress (step completion, driver movement)
   */
  simulateRouteProgress() {
    const progressEvents = [
      {
        event: 'route.step.completed',
        data: {
          routeId: Math.floor(Math.random() * 5) + 1,
          stepId: Math.floor(Math.random() * 100) + 100,
          timestamp: new Date().toISOString(),
        },
      },
      {
        event: 'route.driver.location',
        data: {
          driverId: Math.floor(Math.random() * 5) + 1,
          lat: 46.2 + (Math.random() - 0.5) * 0.02,
          lng: 6.14 + (Math.random() - 0.5) * 0.02,
          timestamp: new Date().toISOString(),
        },
      },
    ];

    // Randomly emit one of these events (50% chance)
    if (Math.random() > 0.5) {
      const randomEvent = progressEvents[Math.floor(Math.random() * progressEvents.length)];
      console.log(`[DEMO MODE] Simulating event: ${randomEvent.event}`);
      this.trigger(randomEvent.event, randomEvent.data);
    }
  }

  /**
   * Simulate order status changes
   */
  simulateOrderStatusUpdate() {
    if (Math.random() > 0.7) {
      const statuses = ['pickup_arrived', 'collected', 'in_transit', 'delivery_arrived', 'completed'];

      const event = {
        event: 'order.status.changed',
        data: {
          orderId: Math.floor(Math.random() * 10) + 1,
          status: statuses[Math.floor(Math.random() * statuses.length)],
          timestamp: new Date().toISOString(),
        },
      };

      console.log(`[DEMO MODE] Simulating event: ${event.event}`);
      this.trigger(event.event, event.data);
    }
  }
}

// Create singleton instance
let mockSocketInstance = null;

/**
 * Get or create mock socket instance
 */
export function getMockSocket() {
  if (!mockSocketInstance) {
    mockSocketInstance = new MockSocket();
  }
  return mockSocketInstance;
}

/**
 * Factory function to create socket connection
 * Returns mock socket in demo mode, otherwise returns real socket.io connection
 */
export function createSocket() {
  if (isDemoMode) {
    console.log('[DEMO MODE] Creating mock socket instead of real connection');
    return getMockSocket();
  }

  // In production/dev, return real socket.io
  // This will be imported from socket.io-client in the actual code
  return null; // Caller should handle real socket creation
}

export { isDemoMode };
